import PublicAdminPanelInstance from "../../api/PublicAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

export const REGULATOR_PASSWORD_POLICY_CHECK_PATH =
  "/admin-panel-regulators/v1/password-policy/check";

export const PASSWORD_POLICY_RULE_IDS = [
  "min_length",
  "requires_lowercase",
  "requires_uppercase",
  "requires_number",
  "requires_symbol",
  "not_email_similar",
] as const;

export type PasswordPolicyRuleId = (typeof PASSWORD_POLICY_RULE_IDS)[number];
export type PasswordPolicyAudience = "staff" | "regulator";

export type PasswordPolicyRule = {
  id: PasswordPolicyRuleId;
  msg_code: string;
  passed: boolean;
};

export type PasswordPolicyResult = {
  all_passed: boolean;
  min_length: number;
  accepted_symbols: string;
  rules: PasswordPolicyRule[];
};

export type PasswordPolicyCheckPayload = {
  email: string;
  new_password: string;
};

export const checkPasswordPolicy = async (
  audience: PasswordPolicyAudience,
  payload: PasswordPolicyCheckPayload,
  signal?: AbortSignal,
): Promise<PasswordPolicyResult> => {
  const path =
    audience === "regulator"
      ? REGULATOR_PASSWORD_POLICY_CHECK_PATH
      : API_ROUTES.PASSWORD_POLICY_CHECK;
  const response = await PublicAdminPanelInstance.post(path, payload, {
    signal,
  });
  return response.data.data;
};

export const isPasswordPolicyResult = (
  value: unknown,
): value is PasswordPolicyResult => {
  if (!value || typeof value !== "object") return false;
  const result = value as Partial<PasswordPolicyResult>;
  if (
    typeof result.all_passed !== "boolean" ||
    !Number.isInteger(result.min_length) ||
    Number(result.min_length) <= 0 ||
    typeof result.accepted_symbols !== "string" ||
    !result.accepted_symbols ||
    !Array.isArray(result.rules) ||
    result.rules.length !== PASSWORD_POLICY_RULE_IDS.length
  ) {
    return false;
  }

  const rulesById = new Map(
    result.rules.map((rule) => [rule?.id, rule]),
  );
  return (
    PASSWORD_POLICY_RULE_IDS.every((id) => {
      const rule = rulesById.get(id);
      return (
        rule?.id === id &&
        typeof rule.msg_code === "string" &&
        Boolean(rule.msg_code) &&
        typeof rule.passed === "boolean"
      );
    }) &&
    result.all_passed === result.rules.every((rule) => rule.passed)
  );
};
